'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

export type FormField = {
  fieldType: 'text' | 'email' | 'phone' | 'textarea'
  fieldName: string
  placeholder: string
  label?: string
  required: boolean
  width: 'half' | 'full'
}

export type HelpLink = {
  linkText: string
  linkUrl: string
}

export type FormBlock4Type = {
  blockName?: string
  blockType?: 'formBlock4'
  title: string
  subtitle?: string
  phoneText?: string
  phoneNumber?: string
  formFields: FormField[]
  submitButtonText: string
  submitButtonIcon: boolean
  enableHelpSection: boolean
  helpTitle?: string
  helpDescription?: SerializedEditorState
  email?: string
  phone?: string
  helpLinks?: HelpLink[]
  backgroundColor: 'white' | 'gray' | 'blue'
  formAction?: string
  successMessage: string
  className?: string
}

export const FormBlock4: React.FC<
  {
    id?: string
  } & FormBlock4Type
> = (props) => {
  const {
    title,
    subtitle,
    phoneText,
    phoneNumber,
    formFields,
    submitButtonText,
    submitButtonIcon,
    enableHelpSection,
    helpTitle,
    helpDescription,
    email,
    phone,
    helpLinks,
    backgroundColor,
    formAction,
    successMessage,
    className,
  } = props

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  const onSubmit = async (data: Record<string, unknown>) => {
    setIsLoading(true)
    setError(null)

    try {
      if (formAction) {
        const response = await fetch(formAction, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })

        if (!response.ok) {
          throw new Error('Failed to submit form')
        }
      }

      setIsSubmitted(true)
      reset()
    } catch (_err) {
      setError('Failed to submit form. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const backgroundClasses = {
    white: 'bg-white',
    gray: 'bg-gray-50',
    blue: 'bg-blue-50',
  }

  const renderFormField = (field: FormField, index: number) => {
    const inputClasses = 'border-2 rounded px-3 py-1 w-full focus:border-indigo-400 input'
    const fieldError = errors[field.fieldName]

    const inputProps = {
      ...register(field.fieldName, {
        required: field.required ? `${field.placeholder} is required` : false,
        ...(field.fieldType === 'email' && {
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address',
          },
        }),
      }),
      placeholder: field.placeholder,
      className: cn(inputClasses, fieldError && 'border-red-500'),
    }

    return (
      <div
        key={index}
        className={cn('px-3 mb-6', field.width === 'half' ? 'sm:w-1/2' : 'sm:w-full')}
      >
        {field.label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {field.fieldType === 'textarea' ? (
          <textarea {...inputProps} rows={4} />
        ) : (
          <input {...inputProps} type={field.fieldType} />
        )}

        {fieldError && <p className="text-red-500 text-sm mt-1">{fieldError.message as string}</p>}
      </div>
    )
  }

  if (isSubmitted) {
    return (
      <div className={cn('contact-1 py-4 md:py-12', backgroundClasses[backgroundColor], className)}>
        <div className="container mx-auto px-4">
          <div className="xl:flex -mx-4">
            <div className="xl:w-10/12 xl:mx-auto px-4">
              <div className="text-center py-8">
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  <h2 className="text-2xl font-semibold mb-2">Success!</h2>
                  <p>{successMessage}</p>
                </div>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="mt-4 text-indigo-600 hover:text-indigo-800 underline"
                >
                  Send another message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('contact-1 py-4 md:py-12', backgroundClasses[backgroundColor], className)}>
      <div className="container mx-auto px-4">
        <div className="xl:flex -mx-4">
          <div className="xl:w-10/12 xl:mx-auto px-4">
            <div className="xl:w-3/4 mb-4">
              <h1 className="text-3xl text-medium mb-4">{title}</h1>
              {subtitle && <p className="text-xl mb-2">{subtitle}</p>}
              {phoneText && phoneNumber && (
                <p>
                  {phoneText}{' '}
                  <a
                    href={`tel:${phoneNumber}`}
                    className="text-indigo-600 border-b border-transparent hover:border-indigo-600 transition-colors duration-300"
                  >
                    {phoneNumber}
                  </a>
                </p>
              )}
            </div>

            <div className="md:flex md:-mx-4 mt-4 md:mt-10">
              <div className="md:w-2/3 md:px-4">
                <div className="contact-form">
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="sm:flex sm:flex-wrap -mx-3">
                      {formFields.map((field, index) => renderFormField(field, index))}
                    </div>

                    <div className="text-right mt-4 md:mt-12">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="border-2 border-indigo-600 rounded px-6 py-2 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Sending...' : submitButtonText}
                        {submitButtonIcon && !isLoading && (
                          <i className="fas fa-chevron-right ml-2 text-sm"></i>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {enableHelpSection && (
                <div className="md:w-1/3 md:px-4 mt-10 md:mt-0">
                  <div className="bg-indigo-100 rounded py-4 px-6">
                    {helpTitle && <h5 className="text-xl font-medium mb-3">{helpTitle}</h5>}

                    {helpDescription && (
                      <div className="text-gray-700 mb-4">
                        <RichText data={helpDescription} />
                      </div>
                    )}

                    <div className="text-gray-700">
                      {email && phone && (
                        <p className="mb-4">
                          Need help or have any query? Don&apos;t hesitate, you can directly shoot
                          us an{' '}
                          <a
                            href={`mailto:${email}`}
                            className="text-indigo-600 border-b border-transparent hover:border-indigo-600 inline-block"
                          >
                            email
                          </a>{' '}
                          or call us at{' '}
                          <a
                            href={`tel:${phone}`}
                            className="text-indigo-600 border-b border-transparent hover:border-indigo-600 inline-block"
                          >
                            {phone}
                          </a>
                        </p>
                      )}

                      {helpLinks && helpLinks.length > 0 && (
                        <p>
                          You can move to{' '}
                          {helpLinks.map((link, index) => (
                            <span key={index}>
                              <a
                                href={link.linkUrl}
                                className="text-indigo-600 border-b border-transparent hover:border-indigo-600 inline-block"
                              >
                                {link.linkText}
                              </a>
                              {index < helpLinks.length - 1 && <span> or </span>}
                            </span>
                          ))}{' '}
                          page to get more information about our site.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
